import { Fragment, useEffect, useRef, useState } from 'react';
import type { ReactNode, Ref } from 'react';
import { BandPill, Card, Chip } from '@/components/ui';
import { cx } from '@/components/ui/cx';
import { useT } from '@/i18n';
import { BAND_STYLE } from '@/styles/tokens';
import type { Block, OpenPage, PageNumber, ViewableDocument } from '@/api/types';
import { LockedPage } from './LockedPage';
import { PILLAR_ACCENT, Radar, SceneIcon } from './programme';
import { BarsBlock, CoverBlock, FridgeBlock, GlanceBlock, GoalsBlock, NoteBlock, RolesBlock, RoutineBlock, TableBlock } from './planSections';

export interface DocumentViewProps {
  /** An area `ReportDocument` or the session `ProgrammeDocument` — both are just pages. */
  document: ViewableDocument;
  onUnlock?: () => void;
  price?: string;
}

type ScoreMeter = NonNullable<Extract<Block, { kind: 'score' }>['meter']>;

const Meter = ({ meter }: { meter: ScoreMeter }) => {
  const t = useT();
  const span = meter.max - meter.min || 1;
  const pct = (v: number) => `${Math.max(0, Math.min(100, ((v - meter.min) / span) * 100))}%`;
  const [a, b] = meter.cuts;
  const order = meter.reversed ? (['discuss', 'watch', 'ok'] as const) : (['ok', 'watch', 'discuss'] as const);
  const segments = [
    { left: '0%', width: pct(a) },
    { left: pct(a), width: `${(((b - a) / span) * 100).toFixed(1)}%` },
    { left: pct(b), right: '0' },
  ];
  const label = t('report.meterLabel', { value: meter.value, min: meter.min, max: meter.max });

  return (
    <div>
      <div role="img" aria-label={label} className="relative h-1.5 rounded-full bg-sage-200">
        {order.map((band, i) => (
          <span
            key={band}
            className="absolute top-0 bottom-0 rounded-full"
            style={{ ...segments[i], backgroundColor: BAND_STYLE[band].dot }}
          />
        ))}
      </div>
      <div className="mt-1 flex justify-between text-caption text-ink-muted" aria-hidden="true">
        <span>{meter.min}</span>
        <span>{meter.max}</span>
      </div>
    </div>
  );
};

const Rows = ({ items }: { items: Array<{ label: string; value: string }> }) => (
  <dl className="divide-y divide-sage-200">
    {items.map((row, i) => (
      <div key={i} className="flex min-h-tap items-center justify-between gap-4 py-1">
        <dt className="text-body text-ink-muted">{row.label}</dt>
        <dd className="text-body font-bold text-ink">{row.value}</dd>
      </div>
    ))}
  </dl>
);

const RenderBlock = ({ block }: { block: Block }) => {
  const t = useT();

  switch (block.kind) {
    case 'paragraph':
      return <p className="text-body">{block.text}</p>;

    case 'heading':
      return <h3>{block.text}</h3>;

    case 'callout':
      return (
        <Card tone={block.tone}>
          <p className="text-body">{block.text}</p>
        </Card>
      );

    case 'band':
      return (
        <Card className="flex items-center justify-between gap-4">
          <div>
            <p className="text-label text-ink">{block.label}</p>
            <p className="text-body text-ink-soft">{block.line}</p>
          </div>
          <BandPill band={block.band} label={block.bandLabel} />
        </Card>
      );

    case 'chips':
      return (
        <ul role="list" className="flex flex-wrap gap-2">
          {block.items.map((item) => (
            <li key={item} role="listitem">
              <Chip>{item}</Chip>
            </li>
          ))}
        </ul>
      );

    case 'rows':
      return <Rows items={block.items} />;

    case 'answers':
      return (
        <>
          {block.groups.map((group, gi) => {
            const headingId = `report-answers-${gi}-${group.label}`;
            return (
              <Card key={gi} className="space-y-2">
                <div className="flex items-center justify-between gap-4">
                  <h4 id={headingId} className="text-label text-ink">
                    {group.label}
                  </h4>
                  <span className="text-caption text-ink-muted">
                    {t('report.answered', { n: group.answered })}
                  </span>
                </div>
                <ul role="list" aria-labelledby={headingId} className="divide-y divide-sage-200">
                  {group.items.map((item, ii) => (
                    <li key={ii} role="listitem" className="flex items-center justify-between gap-4 py-1 text-body">
                      <span className="text-ink-soft">{item.text}</span>
                      <span className="font-bold text-attention-ink">{item.answer}</span>
                    </li>
                  ))}
                </ul>
              </Card>
            );
          })}
        </>
      );

    case 'activity':
      return (
        <Card className="space-y-2">
          <h4 className="text-label text-ink">
            {block.index}. {block.title}
          </h4>
          <ol className="list-decimal space-y-1 pl-5 text-body text-ink-soft">
            {block.steps.map((step, i) => (
              <li key={i}>{step}</li>
            ))}
          </ol>
          <p className="text-caption italic text-ink-muted">{t('report.whenHard', { text: block.whenHard })}</p>
        </Card>
      );

    case 'tracker':
      return (
        <table className="w-full border-collapse text-body">
          <caption className="mb-2 text-left text-label text-ink">{t('report.trackerCaption')}</caption>
          <thead>
            <tr>
              <th scope="col" className="border border-sage-300 bg-teal-100 p-2 text-left text-label text-teal-700">
                {t('report.activityHeader')}
              </th>
              {[1, 2, 3, 4].map((n) => (
                <th key={n} scope="col" className="border border-sage-300 bg-teal-100 p-2 text-label text-teal-700">
                  {t('report.week', { n })}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {block.activities.map((activity, i) => (
              <tr key={i}>
                <td className="border border-sage-300 p-2 text-left font-bold text-ink">{activity}</td>
                {[1, 2, 3, 4].map((n) => (
                  <td key={n} className="border border-sage-300 p-2 text-center">
                    <input
                      type="checkbox"
                      disabled
                      aria-hidden="true"
                      tabIndex={-1}
                      className="h-4 w-4 rounded-sm border-teal-500"
                    />
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      );

    case 'lines':
      return (
        <div className="space-y-4">
          {block.labels.map((label, i) => (
            <div key={i}>
              <p className="text-caption text-ink-muted">{label}</p>
              <div className="mt-2 h-px w-full bg-sage-300" />
            </div>
          ))}
        </div>
      );

    case 'score':
      return (
        <Card className="space-y-3">
          <div className="flex items-center justify-between gap-4">
            <p className="text-h2">{block.headline}</p>
            <BandPill band={block.band} label={block.bandLabel} />
          </div>
          {block.meter && <Meter meter={block.meter} />}
          <Rows items={block.rows} />
        </Card>
      );

    case 'steps':
      return (
        <ol className="list-decimal space-y-1 pl-5 text-body">
          {block.items.map((item, i) => (
            <li key={i}>{item}</li>
          ))}
        </ol>
      );

    case 'pillarIntro': {
      const accent = PILLAR_ACCENT[block.pillar];
      return (
        <div className="space-y-3">
          <Card className="border-l-4" style={{ borderLeftColor: accent }}>
            <p className="text-caption font-bold uppercase tracking-wide text-ink-muted">{t('programme.weekGoal')}</p>
            <p className="mt-1 text-label text-ink">{block.weekGoal}</p>
          </Card>
          <Card className="border-l-4" style={{ borderLeftColor: accent }}>
            <p className="text-caption font-bold uppercase tracking-wide text-ink-muted">{t('programme.focus')}</p>
            <p className="mt-1 text-label text-ink">{block.focus}</p>
          </Card>
          <h3 className="text-label text-ink">{t('programme.activities')}</h3>
          <ol className="list-decimal space-y-1 pl-5 text-body">
            {block.activities.map((activity, i) => (
              <li key={i}>{activity}</li>
            ))}
          </ol>
        </div>
      );
    }

    case 'exercise': {
      const accent = PILLAR_ACCENT[block.pillar];
      return (
        <Card className="space-y-4">
          <div className="flex flex-wrap items-center gap-2">
            <span className="rounded-pill px-2.5 py-0.5 text-caption font-bold text-white" style={{ backgroundColor: accent }}>
              {t('programme.activity', { n: block.index })}
            </span>
            {block.slot && <Chip>{block.slot}</Chip>}
            <Chip>{t('programme.minutes', { n: block.durationMinutes })}</Chip>
            <Chip>{t('programme.level', { n: block.level })}</Chip>
          </div>
          <div className="flex gap-4">
            <SceneIcon scene={block.scene} pillar={block.pillar} label={t('programme.illustration', { title: block.title })} className="h-24 w-24 shrink-0" />
            <Card tone="sage" className="flex-1">
              <p className="text-body">{block.why}</p>
            </Card>
          </div>
          <div>
            <h3 className="text-label text-ink">{t('programme.materials')}</h3>
            <p className="text-body text-ink-soft">{block.materials.length > 0 ? block.materials.join(', ') : t('programme.nothingSpecial')}</p>
          </div>
          <div>
            <h3 className="text-label text-ink">{t('programme.how')}</h3>
            <ol className="mt-1 list-decimal space-y-1 pl-5 text-body">
              {block.steps.map((step, i) => (
                <li key={i}>{step}</li>
              ))}
            </ol>
          </div>
          <div className="grid gap-3 sm:grid-cols-2">
            <div className="rounded-md border-t-4 border-progress bg-cream p-3">
              <p className="text-caption font-bold uppercase tracking-wide text-ink-muted">{t('programme.lookFor')}</p>
              <p className="mt-1 text-body">{block.successSignal}</p>
            </div>
            <div className="rounded-md border-t-4 border-attention bg-cream p-3">
              <p className="text-caption font-bold uppercase tracking-wide text-ink-muted">{t('programme.whenHard')}</p>
              <p className="mt-1 text-body">{block.whenHard}</p>
            </div>
          </div>
          {block.caution && (
            <Card tone="apricot">
              <p className="text-body">{t('programme.caution', { text: block.caution })}</p>
            </Card>
          )}
        </Card>
      );
    }

    case 'radar':
      return (
        <div className="space-y-2">
          <Radar
            axes={block.axes}
            reference={block.reference}
            label={t('programme.radarLabel', { values: block.axes.map((a) => `${a.label} ${a.value}%`).join(', ') })}
          />
          <p className="text-caption text-ink-muted">{t('programme.radarLegend', { reference: block.reference })}</p>
        </div>
      );

    case 'timeline':
      return (
        <ul role="list" className="divide-y divide-sage-200">
          {block.items.map((item, i) => (
            <li key={i} role="listitem" className="flex items-baseline gap-3 py-2 text-body">
              <span className="w-24 shrink-0 text-caption font-bold uppercase tracking-wide text-teal-600">{item.domain}</span>
              <span>{item.skill}</span>
            </li>
          ))}
        </ul>
      );

    case 'cover':
      return <CoverBlock block={block} />;
    case 'glance':
      return <GlanceBlock block={block} />;
    case 'bars':
      return <BarsBlock block={block} />;
    case 'goals':
      return <GoalsBlock block={block} />;
    case 'routine':
      return <RoutineBlock block={block} />;
    case 'fridge':
      return <FridgeBlock block={block} />;
    case 'table':
      return <TableBlock block={block} />;
    case 'roles':
      return <RolesBlock block={block} />;
    case 'note':
      return <NoteBlock block={block} />;
  }
};

const OpenPageSection = ({ page, total, sectionRef }: { page: OpenPage; total: number; sectionRef: Ref<HTMLElement> }) => {
  const t = useT();
  const headingId = `report-page-${page.number}-heading`;
  const isCover = page.blocks[0]?.kind === 'cover';

  return (
    <section ref={sectionRef} data-page-number={page.number} aria-labelledby={headingId} className="scroll-mt-24">
      {/* A programme cover draws its own title, so its page heading is for screen readers only. */}
      <h2 id={headingId} className={cx(isCover && 'sr-only')}>
        <span className="block text-caption text-ink-muted">{t('report.pages', { n: page.number, total })}</span>
        {page.title}
      </h2>
      {page.subtitle && !isCover && <p className="text-caption text-ink-muted">{page.subtitle}</p>}
      <div className="mt-4 space-y-4">
        {page.blocks.map((block, i) => (
          <Fragment key={i}>
            <RenderBlock block={block} />
          </Fragment>
        ))}
      </div>
    </section>
  );
};

const navPillClass = (active: boolean) =>
  cx(
    'inline-flex h-9 min-w-9 items-center justify-center rounded-pill px-3 text-label transition-colors',
    active ? 'bg-teal-600 text-white' : 'bg-sage-100 text-ink-muted hover:text-teal-600',
  );

/**
 * Renders `document.pages` in order — open pages as full block sections, locked pages as
 * `LockedPage` stubs — behind a sticky page nav whose `aria-current="page"` pill tracks scroll
 * position via `IntersectionObserver`. Mirrors the backend's `src/pdf/html.ts` block-for-block so
 * the web viewer and the PDF never drift.
 */
export const DocumentView = ({ document: doc, onUnlock, price }: DocumentViewProps): ReactNode => {
  const t = useT();
  const sectionRefs = useRef<Partial<Record<PageNumber, HTMLElement | null>>>({});
  const [activePage, setActivePage] = useState<PageNumber>(doc.pages[0]?.number ?? 1);

  useEffect(() => {
    if (typeof IntersectionObserver === 'undefined') return undefined;

    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((entry) => entry.isIntersecting)
          .sort((a, b) => b.intersectionRatio - a.intersectionRatio)[0];
        if (!visible) return;
        const n = Number((visible.target as HTMLElement).dataset.pageNumber) as PageNumber;
        if (!Number.isNaN(n)) setActivePage(n);
      },
      { rootMargin: '-40% 0px -50% 0px', threshold: [0, 0.25, 0.5, 0.75, 1] },
    );

    Object.values(sectionRefs.current).forEach((el) => el && observer.observe(el));
    return () => observer.disconnect();
  }, [doc.pages]);

  const firstLockedNumber = doc.pages.find((page) => page.locked)?.number;

  const scrollToPage = (n: PageNumber) => {
    sectionRefs.current[n]?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  return (
    <div>
      <nav aria-label={t('report.pageNav')} className="sticky top-0 z-10 -mx-4 flex gap-2 overflow-x-auto bg-cream/95 px-4 py-2 backdrop-blur nav:mx-0 nav:px-0">
        {doc.pages.map((page) => (
          <button
            key={page.number}
            type="button"
            aria-current={page.number === activePage ? 'page' : undefined}
            onClick={() => scrollToPage(page.number)}
            className={navPillClass(page.number === activePage)}
          >
            {page.number}
          </button>
        ))}
      </nav>

      <div className="mt-6 space-y-10">
        {doc.pages.map((page) =>
          page.locked ? (
            <LockedPage
              key={page.number}
              page={page}
              showUnlock={page.number === firstLockedNumber}
              onUnlock={onUnlock}
              price={price}
              sectionRef={(el) => {
                sectionRefs.current[page.number] = el;
              }}
            />
          ) : (
            <OpenPageSection
              key={page.number}
              page={page}
              total={doc.pages.length}
              sectionRef={(el) => {
                sectionRefs.current[page.number] = el;
              }}
            />
          ),
        )}
      </div>
    </div>
  );
};
